<template>
  <tr :class="['order-item-row', `order-item-row-group-${item.groupStockType}`, isItemInNCRLockMode ? 'order-item-row-ncr-locked' : '', isItemCancelled ? 'order-item-row-cancelled' : '']">
    <td :class="['order-item-type-details']">
      <div class="order-item-row-index-marker" v-if="rootItemIndex != null">{{rootItemIndex+1}}</div>
      <span v-if="isChildItem"><span v-html="indentDisplay" /><i class="fas fa-caret-right fa-sm text-muted order-item-indent-arrow"></i>&nbsp;&nbsp;</span>
      <span :class="{'font-weight-bold': !isChildItem, 'order-item-type': true}">{{item.stockType | formatLookupName(InventoryStockTypeLookup)}}</span>
      <br />
      <div class="order-item-icon-list">
        <span v-html="indentDisplay" />

        <OrderItemIcon
          name="Notes"
          :editing="isEditingMode"
          :label="item.notes"
          :value="item.notes != ''"
          icon="fa-sticky-note"
          iconClass="order-item-icon-note"
          @click="displayNotesPopover" />

        <OrderItemIcon
          name="Custom Fields"
          v-if="!isChildItem && optionCustomFieldsAvailable"
          :editing="isEditingMode"
          :label="customFieldDisplayData"
          :value="hasCustomFields"
          icon="fa-table"
          iconClass="order-item-icon-custom-field"
          @click="displayCustomFieldsPopover" />

        <OrderItemIcon
          name="Customer Approval"
          v-if="!isChildItem && optionCustomerApprovalAvailable"
          :editing="isEditingMode"
          :label="item.requiresCustomerApproval ? 'Requires Approval' : ''"
          :value="item.requiresCustomerApproval"
          icon="fa-user-circle"
          iconClass="order-item-icon-customer-approval"
          @click="item.requiresCustomerApproval = !item.requiresCustomerApproval; itemListUpdated()" />

        <OrderItemIcon
          name="Discount"
          v-if="optionDiscountAvailable"
          :editing="isEditingMode"
          :label="item.discountRate > 0 ? `${item.discountRate}% Discount` : ''"
          :value="item.discountRate > 0"
          icon="fa-percent"
          iconClass="order-item-icon-discount"
          @click="displayDiscountPopover" />

        <OrderItemIcon
          name="Edgework"
          v-if="isItemEdgeworkService && optionEdgeworkAvailable && !isEditorGeneratedItem"
          :editing="isEditingMode && isEdgeworkEditable"
          :label="itemEdgeworkLabel(item)"
          :value="item.edgeworkFlagList && item.edgeworkFlagList.length > 0"
          icon="fa-border-outer"
          iconClass="order-item-icon-edgework"
          @click="displayEdgeworkPopover" />

        <OrderItemIcon
          v-if="isItemExternalAvailable && optionExternalAvailable"
          :name="isEntireRootItemRequireExternal() ? 'Entire Item Externally Sourced' : 'Externally Sourced'"
          :editing="isEditingMode || item.isExternal"
          :value="item.isExternal"
          :icon="isEntireRootItemRequireExternal() ? 'fa-external-link-alt' : 'fa-external-link-square-alt'"
          :iconClass="isEntireRootItemRequireExternal() ? 'order-item-icon-external-all' : 'order-item-icon-external'"
          @click="externalClicked" />

        <OrderItemIcon
          name="NCR"
          v-if="isItemNCRMode || isItemFlaggedNCR"
          :editing="true"
          label="Non Chargeable Rework"
          :value="true"
          icon="fa-repeat"
          :iconClass="ncrIconClass"
          @click="displayNCRPopover" />

        <OrderItemIcon
          name="NCR Repair"
          v-if="isNCR"
          :editing="isEditingMode"
          label="NCR Repair"
          :value="item.ncrIsRepair"
          icon="fa-tools"
          iconClass="order-item-icon-ncr-repair"
          @click="ncrRepairClicked" />

        <OrderItemIcon
          name="Block"
          v-if="isGlassSizeSelected && item.isBlock && isSupplierPricing"
          :editing="false"
          :label="`Block Size: ${item.blockSize}`"
          :value="true"
          icon="fa-cube"
          iconClass="order-item-icon-block" />

        <OrderItemIcon
          name="Editor Notes"
          v-if="isEditorGeneratedItem && item.orderEditorNotes && item.orderEditorNotes != ''"
          :editing="false"
          :label="item.orderEditorNotes"
          :value="item.orderEditorNotes != ''"
          icon="fa-clipboard"
          iconClass="order-item-icon-editor" />

        <OrderItemIcon
          name="Editor Generated"
          v-if="isEditorGeneratedItem && !isChildItem"
          :editing="false"
          label="Generated by the glass editor. Item must be edited via the glass editor"
          :value="true"
          iconStyle="fad"
          icon="fa-shapes"
          iconClass="order-item-icon-editor" />

        <OrderItemIcon
          name="Despatched"
          v-if="hasDespatchedQuantity && (isGlassItem || isTemplateItem || isAssemblyItem)"
          :editing="false"
          :label="`${orderItem.despatchedQuantity} / ${orderItem.quantity} Despatched`"
          :value="true"
          iconStyle="far"
          icon="fa-truck"
          iconClass="order-item-icon-despatched" />

        <OrderItemIcon
          name="Invoiced"
          v-if="hasInvoicedQuantity && (isGlassItem || isTemplateItem || isRootItem)"
          :editing="false"
          :label="`${orderItem.invoicedQuantity} / ${orderItem.quantity} Invoiced`"
          :value="true"
          iconStyle="fas"
          icon="fa-envelope-open-dollar"
          iconClass="order-item-icon-invoiced" />

        <OrderItemIcon
          name="Manually Added Item"
          v-if="isCustomAddedItemForEditorParent && isChildItem"
          :editing="false"
          label="Manually added item under an order item generated by the glass editor"
          :value="true"
          iconStyle="fas"
          icon="fa-plus-circle"
          iconClass="order-item-icon-manually-added" />

        <OrderItemIcon
          name="Template Handling Required"
          v-if="item.isTemplateHandlingRequired"
          :editing="false"
          label="Item requires template handling in production"
          :value="true"
          iconStyle="fas"
          icon="fa-scanner-image"
          iconClass="order-item-icon-template-handling" />

        <OrderItemIcon
          :label="`Total Item Weight: ${formatDecimal(itemTotalWeight, 0, true)}kg`"
          v-if="isGlassItem && isSupplierPricing"
          :value="true"
          :editing="false"
          icon="fa-weight"
          iconStyle="far"
          iconClass="order-item-icon-note" />

        <OrderItemIcon
          label="Price is above the default price"
          v-if="isUnitPriceAboveOriginalPrice"
          :value="true"
          :editing="false"
          icon="fa-usd-circle"
          iconStyle="fas"
          iconClass="order-item-icon-price-above"
          iconStackClass="order-item-icon-price-above-bg" />

        <OrderItemIcon
          :label="item.priceError + (item.priceLocalError||'')"
          v-if="item.priceError || item.priceLocalError"
          :value="item.priceError != '' || item.priceLocalError != ''"
          :editing="true"
          icon="fa-usd-circle"
          iconStyle="fas"
          iconClass="order-item-icon-price-error"
          iconStackClass="order-item-icon-price-error-bg"
          @click="displayPriceErrorPopover" />

        <OrderItemIcon
          label="Production Warnings. Click to view details"
          v-if="item.productionError || item.productionConfigError || item.productionStockError || item.productionEditorError"
          :value="item.productionError != '' || item.productionConfigError != '' || item.productionStockError != '' || item.productionEditorError != ''"
          :editing="true"
          icon="fa-exclamation-triangle"
          iconStyle="fas"
          iconClass="order-item-icon-production-error"
          iconStackClass="order-item-icon-production-error-bg"
          @click="displayProductionErrorPopover" />

        <OrderItemIcon
          label="Item has been cancelled"
          v-if="isItemCancelled && !isChildItem"
          :value="true"
          :editing="true"
          icon="fa-times-octagon"
          iconStyle="fas"
          iconClass="order-item-icon-cancelled"
          iconStackClass="order-item-icon-cancelled-bg"
          @click="displayItemCancelledPopover" />
      </div>

      <Popover ref="notesPopover" :showHeader="true" headerTitle="Notes">
        <div class="order-item-popover-content">
          <FormInputTextArea ref="notesEntry" v-model="item.notes" @input="itemListUpdated()" placeholder="Item Notes" />
        </div>
      </Popover>
      <Popover ref="customFieldsPopover" :showHeader="true" headerTitle="Custom Fields">
        <div class="order-item-popover-content">
          <div v-if="!isCustomFieldLookupEmpty">
            <StaticDataTable class="static-table-list" :striped="false" :smallRows="true">
              <template v-slot:header>
                <tr>
                  <th width="38%">Field</th>
                  <th width="58%">Value</th>
                  <th> </th>
                </tr>
              </template>
              <template v-slot:body>
                <tr v-for="(fieldInfo, index) of customFieldEditList" :key="index">
                  <td><FormSelect class="form-input-sm" placeholder="Field" :options="customFieldLookup" :value="fieldInfo.key" @input="updateCustomFieldKey(index, $event)" /></td>
                  <td><FormInputText class="form-input-sm" placeholder="Value" :value="fieldInfo.value" @input="updateCustomFieldValue(index, $event)" /></td>
                  <td><FormButton class="btn-default btn-xs" @click.stop="removeCustomFieldRow(index)"><i class="far fa-trash-alt"></i></FormButton></td>
                </tr>
              </template>
            </StaticDataTable>
            <FormButton class="btn-default btn-xs" @click="addCustomFieldRow"><i class="far fa-plus-circle"></i> Add</FormButton>
          </div>
          <div v-if="isCustomFieldLookupEmpty">No custom fields available. Please add fields in order settings</div>
        </div>
      </Popover>
      <Popover ref="discountPopover" :showHeader="true" headerTitle="Discount">
        <div class="order-item-popover-content">
          <FormInputText type="number" ref="discountRateEntry" v-model="item.discountRate" @input="itemListUpdated()" placeholder="Discount" addon="%" />
        </div>
      </Popover>

      <OrderItemEdgeworkPopover ref="edgeworkPopover" v-model="item.edgeworkFlagList" @update="itemListUpdated()" />

      <OrderItemNCRPopover ref="ncrPopover" :item="item" />

      <OrderItemExternalPopover ref="externalPopover" :item="item" />

      <Popover ref="priceErrorPopover" :showHeader="true" headerTitle="Price Notice">
        <div class="order-item-popover-content order-item-error-popover-content">
          <ul v-html="convertNewlinesToListItems(item.priceError + '\n' + (item.priceLocalError||''))" class="popover-list"></ul>
        </div>
      </Popover>

      <Popover ref="productionErrorPopover" :showHeader="true" headerTitle="Production Error">
        <div class="order-item-popover-content order-item-error-popover-content">
          <ul v-html="convertNewlinesToListItems(item.productionError + '\n' + item.productionConfigError + '\n' + item.productionStockError + '\n' + item.productionEditorError)" class="popover-list"></ul>
        </div>
      </Popover>

      <Popover ref="cancelledItemPopover" :showHeader="true" headerTitle="Item Cancelled">
        <div class="order-item-popover-content order-item-error-popover-content">
          Notes:<br />
          <ul v-html="convertNewlinesToListItems(item.cancelledReason)" class="popover-list"></ul>
          <p>Cancellation Charged: {{item.chargeCancelFees | formatBoolean}}</p>
        </div>
      </Popover>
    </td>
    <td>
      <FormSearchableSelect
        v-show="isEditingMode && !item.isFixed && !item.isStockLocked"
        ref="stockEntry"
        :options="filteredStockLookupList"
        :value="item.stockId"
        :clearable="false"
        :dropdownAutoWidth="true"
        @input="setItemStockId($event)"
        selectedDisplayKey="code"
        placeholder="Stock Code" />
      <div v-if="!isEditingMode && item.isCustomerOwnStock" class="font-weight-bold">Customers Own {{item.stockType | formatLookupName(InventoryStockTypeLookup)}}</div>
      <LinkStock v-if="(!isEditingMode || item.isFixed || item.isStockLocked) && item.stockId" :id="item.stockId" :name="item.stock.code" />
      <a v-if="isItemStockUnlockable" class="order-item-editable-button ml-2" @click.prevent="unlockItemStock()"><i class="far fa-pencil"></i></a>
    </td>
    <template v-if="!item.stockId || item.loading">
      <td colspan="10">
        <Spinner v-if="item.loading" />
      </td>
    </template>
    <template v-if="item.stockId && !item.loading">
      <td>
        <FormInputText 
          ref="stockDescription"
          v-show="isEditingMode && !item.isFixed && !item.isStockLocked"
          v-model="item.description"
          placeholder="Stock Description"
          @input="itemListUpdated()"
          />
        <LinkStock v-if="(!isEditingMode || item.isFixed || item.isStockLocked) && item.stockId" :id="item.stockId" :name="item.description" />
        <small v-if="item.generatedDescription" class="order-item-generated-description"><i class="fas fa-caret-right"></i> {{item.generatedDescription}}</small>
      </td>
      <td v-if="isPriceGroupsAvailable">
        <FormSearchableSelect
          ref="priceGroupEntry"
          :options="filteredStockPriceGroupLookupList"
          :value="item.stockPriceGroupId"
          :clearable="false"
          :dropdownAutoWidth="true"
          @input="priceGroupUpdated($event)"
          v-show="isEditingMode && isGlassItem && !item.isPriceGroupLocked"
          :filterBy="priceGroupSelectFilter"
        />
        <span v-if="isEditingMode && (!isGlassItem || item.isPriceGroupLocked)" class="order-item-non-editable">{{itemPriceDescription}}</span>
        <span v-if="!isEditingMode" class="order-item-non-editable">{{itemPriceDescription}}</span>
        <a v-if="isItemPriceGroupUnlockable" class="order-item-editable-button ml-2" @click.prevent="unlockItemPriceGroup()"><i class="far fa-pencil"></i></a>
      </td>
      <td>
        <FormInputText
          ref="quantity"
          type="number"
          v-model="item.quantity"
          @input="itemListUpdated()"
          v-show="isQuantityEditable(item) && !item.isQuantityLocked"
          :wholeNumber="isGlassItem || item.uomType == UOMTypes.each"
        />
        <span v-if="!isEditingMode || !isQuantityEditable(item) || item.isQuantityLocked" class="order-item-non-editable">{{item.quantity | formatDecimal}}</span>
        <a v-if="isItemQuantityUnlockable" class="order-item-editable-button ml-2" @click.prevent="unlockItemQuantity()"><i class="far fa-pencil"></i></a>
      </td>

      <td v-if="!(isGlassItem && isStockLinkedPriceGroup)">
        <FormInputText type="number" v-model="item.glassHeight" @input="itemListUpdated()" v-show="isEditingMode && isHeightEditable" :wholeNumber="true" />
        <span v-if="(!isEditingMode && isWidthHeightAvailable) || (isWidthHeightAvailable && !isHeightEditable)" class="order-item-non-editable">{{item.glassHeight}}</span>
        <span v-if="!isWidthHeightAvailable" class="order-item-non-editable">-</span>
        <a v-if="isItemGlassSizeUnlockable" class="order-item-editable-button ml-2" @click.prevent="unlockItemGlassSize()"><i class="far fa-pencil"></i></a>
      </td>

      <td v-if="!(isGlassItem && isStockLinkedPriceGroup)">
        <FormInputText type="number" v-model="item.glassWidth" @input="itemListUpdated()" v-show="isEditingMode && isWidthEditable" :wholeNumber="true" />
        <span v-if="(!isEditingMode && isWidthHeightAvailable) || (isWidthHeightAvailable && !isWidthEditable)" class="order-item-non-editable">{{item.glassWidth}}</span>
        <span v-if="!isWidthHeightAvailable" class="order-item-non-editable">-</span>
        <a v-if="isItemGlassSizeUnlockable" class="order-item-editable-button ml-2" @click.prevent="unlockItemGlassSize()"><i class="far fa-pencil"></i></a>
      </td>

      <td v-if="isGlassItem && isStockLinkedPriceGroup && isCustomerPricing">
        <a v-if="isGlassLevelSelected && isEditingMode && !isEditorGeneratedItem" href="#" class="order-item-editable-link" @click.prevent="$emit('selectGlassClick', item)">{{item.glassHeight}}</a>
        <span v-if="isGlassLevelSelected && (!isEditingMode || isEditorGeneratedItem)" class="order-item-non-editable">{{item.glassHeight}}</span>
        <button class="btn btn-sm btn-white" @click.prevent="$emit('selectGlassClick', item)" v-if="isEditingMode && !isGlassLevelSelected"><i class="far fa-layer-plus"></i> Select Glass</button>
      </td>

      <td v-if="isGlassItem && isStockLinkedPriceGroup && isCustomerPricing">
        <a v-if="isGlassLevelSelected && isEditingMode && !isEditorGeneratedItem" href="#" class="order-item-editable-link" @click.prevent="$emit('selectGlassClick', item)">{{item.glassWidth}}</a>
        <span v-if="isGlassLevelSelected && (!isEditingMode || isEditorGeneratedItem)" class="order-item-non-editable">{{item.glassWidth}}</span>
      </td>

      <td v-if="isGlassItem && isStockLinkedPriceGroup && isSupplierPricing">
        <a v-if="isGlassSizeSelected && isEditingMode" href="#" class="order-item-editable-link" @click.prevent="$emit('selectGlassClick', item)">{{item.glassHeight}}</a>
        <span v-if="isGlassSizeSelected && !isEditingMode" class="order-item-non-editable">{{item.glassHeight}}</span>
        <button class="btn btn-sm btn-white" @click.prevent="$emit('selectGlassClick', item)" v-if="isEditingMode && !isGlassSizeSelected"><i class="far fa-layer-plus"></i> Select Glass</button>
      </td>

      <td v-if="isGlassItem && isStockLinkedPriceGroup && isSupplierPricing">
        <a v-if="isGlassSizeSelected && isEditingMode" href="#" class="order-item-editable-link" @click.prevent="$emit('selectGlassClick', item)">{{item.glassWidth}}</a>
        <span v-if="isGlassSizeSelected && !isEditingMode" class="order-item-non-editable">{{item.glassWidth}}</span>
      </td>

      <td v-if="isMarkAsAvailable">
        <FormInputText v-if="isEditingMode && isMarkAsEditable" ref="itemMark" v-model="item.mark" @input="itemListUpdated()" placeholder="Mark" />
        <span v-if="!isEditingMode && isMarkAsEditable" class="order-item-non-editable">{{item.mark}}</span>
        <span v-if="!isMarkAsEditable" class="order-item-non-editable">-</span>
      </td>
      <td v-if="isUnitsAvailable">
        <template v-if="(!isEditingMode || !isItemUnitsEditable) && !item.isDeliveryItem && !item.isMinPriceItem && !item.isMinPriceSingleItem && !item.isMinInvoiceItem && !isItemPercentageUOMType && !item.isCancelFeeItem">
          <span class="order-item-non-editable">{{item.units | formatDecimal(5)}}</span>
          <span v-if="!item.isDeliveryItem && !item.isMinPriceItem && !item.isMinPriceSingleItem && !item.isMinInvoiceItem && !isItemPercentageUOMType && !item.isCancelFeeItem" v-html="item.uomDisplay"></span>
          <a v-if="isItemUnitsUnlockable" class="order-item-editable-button ml-2" @click.prevent="unlockItemUnits()"><i class="far fa-pencil"></i></a>
        </template>
        <Tooltip v-if="item.unitsNotice" :label="item.unitsNotice">
          &nbsp;<i class="far fa-exclamation-circle order-item-pricing-notice"></i>
        </Tooltip>
        <FormInputText ref="itemUnits" v-model="item.units" @input="itemListUpdated()" v-show="isEditingMode && isItemUnitsEditable" :addon="item.uomDisplay">
        </FormInputText>
      </td>
      <td v-if="isTotalUnitsAvailable">
        <span class="order-item-non-editable" v-if="!item.isDeliveryItem && !item.isMinPriceItem && !item.isMinPriceSingleItem && !item.isMinInvoiceItem && !isItemPercentageUOMType && !item.isCancelFeeItem">{{itemTotalUnits | formatDecimal(5)}}</span>
        <span v-if="!item.isDeliveryItem && !item.isMinPriceItem && !item.isMinPriceSingleItem && !item.isMinInvoiceItem && !isItemPercentageUOMType && !item.isCancelFeeItem" v-html="item.uomDisplay"></span>
      </td>
      <td v-if="isUnitPriceAvailable">
        <Spinner v-if="item.priceLoading && isEditingMode" />
        <FormInputText type="number" ref="itemUnitPrice" v-model="item.unitPrice" @input="itemListUpdated()" v-show="isItemUnitPriceEditable" />
        <template v-if="(!isEditingMode || !item.unitPriceOverridden) && !isPriceHidden">
          <span class="order-item-non-editable">{{item.unitPrice | formatPriceWithUOM(item.uomType)}}</span>
          <a v-if="isItemUnitPriceUnlockable" class="order-item-editable-button ml-2" @click.prevent="unlockItemUnitPrice()"><i class="far fa-pencil"></i></a>
        </template>
        <span v-if="isPriceHidden">-</span>
      </td>
      <td v-if="isLinePriceAvailable">
        <span v-if="!isItemDiscounted && !isPriceHidden" class="font-weight-bold order-item-non-editable">{{item.linePrice | formatCurrency}}</span>
        <span v-if="isPriceHidden">-</span>
        <Tooltip v-if="isItemDiscounted && !isPriceHidden" :label="itemDiscountTooltip">
          <span class="font-weight-bold order-item-lineprice-discount order-item-non-editable">{{item.linePrice | formatCurrency}}</span>
        </Tooltip>
        <Tooltip v-if="item.priceNotice && !isPriceHidden" :label="item.priceNotice">
          &nbsp;<i class="far fa-exclamation-circle order-item-pricing-notice"></i>
        </Tooltip>
      </td>
      <td v-if="isSupplierDetailsAvailable">
        <div v-if="item.isExternal">
          <FormSearchableSelect
            :options="supplierLookupList"
            v-model="item.supplierContactId"
            field="supplierContactId"
            placeholder="Supplier"
            @input="supplierUpdated()"
            v-show="isEditingMode"
            :dropdownAutoWidth="true"
          />
          <LinkContact v-if="!isEditingMode && item.supplierContactId" :id="item.supplierContactId" :name="itemSupplierDescription" />
        </div>
        <div v-if="!item.isExternal">
          Not External
        </div>
      </td>
    </template>
    <td v-if="isEditingMode && showActions">
      <FormButton ref="itemAddServiceBtn" v-if="(isGlassItem || isGlassServiceItem) && optionAddServiceAvailable && isAddServiceChildItemAvailable" class="btn-default btn-xxs btn-keyboard-highlight mr-1" @click="$emit('childItemAdd', InventoryStockTypes.glassService)">
        <i class="far fa-tools"></i>
      </FormButton>

      <ActionDropMenu ref="orderItemActionMain" :tableAction="true" :forceDropdown="true" v-if="!item.loading">
        <DropdownMenuItem @click="$emit('itemRemove')" icon="fa-trash-alt" v-if="isItemRemovable">Remove Item</DropdownMenuItem>
        <DropdownMenuItem @click="$emit('itemCancel')" icon="fa-times" v-if="isItemCancellable">Cancel Item</DropdownMenuItem>

        <DropdownMenuItem @click="itemResetUnitPrice()" v-if="item.unitPriceOverridden" icon="fa-undo-alt">Reset Unit Price</DropdownMenuItem>
        <DropdownMenuItem @click="itemResetUnits()" v-if="!item.isUnitsLocked" icon="fa-undo-alt">Reset Units</DropdownMenuItem>
        <DropdownMenuItem @click="itemResetGlassSize()" v-if="isGlassItem && (hasTemplateParent || hasAssemblyParent) && !item.isGlassSizeLocked" icon="fa-undo-alt">Reset Size</DropdownMenuItem>

        <DropdownMenuItem @click="$emit('childItemAdd', InventoryStockTypes.glassService)" icon="fa-tools" v-if="isAddServiceChildItemAvailable">Add Service Sub-Item</DropdownMenuItem>
        <DropdownMenuItem @click="$emit('childItemAdd', InventoryStockTypes.general)" icon="fa-box-up" v-if="isAddGeneralChildItemAvailable">Add General Sub-Item</DropdownMenuItem>

        <DropdownMenuItem @click="$emit('copySupplierToGroup', item.supplierContactId)" icon="fa-copy" v-if="isSupplierDetailsAvailable && item.isExternal">Copy Supplier To Group</DropdownMenuItem>
      </ActionDropMenu>

      <FormButton ref="itemCopyGroupBtn" v-if="isCopyGroupAvailable" class="btn-default btn-xxs btn-keyboard-highlight ml-1" @click="$emit('copyGroup')">
        <i class="far fa-copy"></i>
      </FormButton>
    </td>

    <td v-if="!isEditingMode && !isItemInNCRLockMode && showActions">
      <FormButton v-if="isItemPreviewAvailable" class="btn-default btn-xxs mr-2" @click="item.showingGlass = !item.showingGlass; itemListUpdated()">
        <i :class="['far', item.showingGlass ? 'fa-eye-slash' : 'fa-eye']"></i>
      </FormButton>
      <ActionDropMenu :tableAction="true" :forceDropdown="true" v-if="!item.loading">
        <DropdownMenuItem @click="$emit('itemCancel')" icon="fa-times" v-if="isItemCancellable">Cancel Item</DropdownMenuItem>
      </ActionDropMenu>
    </td>

    <td v-if="isItemInNCRLockMode && showActions">
      <FormButton v-if="((isGlassItem || isAssemblyItem) && !isChildItem) || isTemplateItem" class="btn-default btn-xxs mr-2" @click="$emit('selectNCRClick', item)">
        <i class="far fa-check"></i> NCR
      </FormButton>
    </td>
  </tr>
</template>


<script>
import _ from 'lodash'
import Utils from '@/utils/utils'
import Popover from '@/components/popover.vue'
import OrderItemEdgeworkPopover from '@/modules/orders/components/orderItemEdgeworkPopover'
import OrderItemNCRPopover from '@/modules/orders/components/orderItemNCRPopover'
import OrderItemNCRDialog from '@/modules/orders/views/orderItemNCRDialog'
import OrderItemExternalPopover from '@/modules/orders/components/orderItemExternalPopover'

import OrderItemIcon from '@/modules/orders/components/orderItemIcon'
import { InventoryStockTypes, InventoryStockTypeLookup, UOMTypes, UOMTypeUnitsLookup, OrderItemEdgeworkTypes, OrderItemEdgeworkTypeLookup, OrderTypes, ApprovalTypes, PriceTypes } from '@/store/types'
import stockPricingMatrixVue from '@/modules/pricing/components/stockPricingMatrix.vue'

export default {
  name: 'OrderItemListItem',
  components: {
    Popover,
    OrderItemEdgeworkPopover,
    OrderItemNCRPopover,
    OrderItemExternalPopover,
    OrderItemIcon
  },
  props: {
    editing: Boolean,
    orderType: String,
    contactId: [Number, String],
    newContactSalesRateId: Number,
    isNewOrder: Boolean,
    rootItemIndex: Number,
    parentItem: Object,
    rootItem: Object,
    orderItem: Object,
    itemList: Array,
    stockLookupList: Array,
    stockPriceGroupList: Array,
    customFieldList: Array,
    showPricing: {
      type: Boolean,
      default: true
    },
    showUnits: {
      type: Boolean,
      default: true
    },
    showPriceGroups: {
      type: Boolean,
      default: true
    },
    showMarkAs: {
      type: Boolean,
      default: true
    },
    showActions: {
      type: Boolean,
      default: true
    },
    priceType: {
      type: String,
      default: PriceTypes.customer
    },
    supplierLookupList: Array,

    // Options to toggle available tools
    optionDiscountAvailable: {
      type: Boolean,
      default: true
    },
    optionCustomFieldsAvailable: {
      type: Boolean,
      default: true
    },
    optionCustomerApprovalAvailable: {
      type: Boolean,
      default: true
    },
    optionExternalAvailable: {
      type: Boolean,
      default: true
    },
    optionEdgeworkAvailable: {
      type: Boolean,
      default: true
    },
    optionNewItemGlassAvailable: {
      type: Boolean,
      default: true
    },
    optionAddServiceAvailable: {
      type: Boolean,
      default: true
    },
    optionAddGeneralAvailable: {
      type: Boolean,
      default: true
    },
    optionBlockRemovingExternal: {
      type: Boolean,
      default: false
    },
    optionSupplierDetailsAvailable: {
      type: Boolean,
      default: false
    },
    optionCancelItemAvailable: {
      type: Boolean,
      default: false
    },
    optionPreviewItemAvailable: {
      type: Boolean,
      default: true
    }
  },
  created() {
    this.UOMTypes = UOMTypes
    this.InventoryStockTypes = InventoryStockTypes
    this.InventoryStockTypeLookup = InventoryStockTypeLookup
  },
  data() {
    return {
      filteredStockLookupList: [],
      filteredStockPriceGroupLookupList: [],
      item: _.cloneDeep(this.$props.orderItem),
    }
  },
  watch: {
    orderItem: {
      deep: true,
      handler: function(newVal, oldVal) {
        // Keep some local-only values
        if(this.item) {
          this.$data.item = _.merge(_.cloneDeep(newVal), {
            loading: this.item.loading,
            priceLoading: this.item.priceLoading
          })
        } else {
          this.$data.item = _.cloneDeep(newVal)
        }

        if(newVal && !oldVal) {
          this.calculateStockLookupList()
        }
      }
    },
    stockLookupList: function() {
      this.calculateStockLookupList()
    },
    stockPriceGroupList: function() {
      this.calculateStockPriceGroupLookupList()
    },
    'orderItem.isDeliveryItem': function() {
      this.calculateStockLookupList()
    },
    'orderItem.stockType': function() {
      this.calculateStockLookupList()
    },
    'orderItem.stockPriceGroupId': function() {
      this.calculateStockLookupList()
    },
    'orderItem.servicePriceRangeId': function() {
      this.calculateStockLookupList()
    },
    'orderItem.stockId': function() {
      this.calculateStockPriceGroupLookupList()
    },
    'orderItem.loading': function() {
      this.updateItemLoadingStatus()
    },
    'orderItem.priceLoading': function() {
      this.updateItemLoadingStatus()
    },
    'parentItem.stockPriceGroupId': function() {
      this.calculateStockLookupList()
    },
    'parentItem.servicePriceRangeId': function() {
      this.calculateStockLookupList()
    },
    contactId: function() {
      // Reload pricing for all parent items
      if(this.item.parentOrderItemId == null)
        this.priceGroupUpdated(this.item.stockPriceGroupId, false)
    }
  },
  mounted() {
    this.calculateStockLookupList()
    this.calculateStockPriceGroupLookupList()
  },
  computed: {
    // NCR
    // When creating an NCR, items are locked down by default until 'unlocked' and included in the order
    isNCR() {
      return this.orderType == OrderTypes.nonChargeableRework
    },
    isItemInNCRLockMode() {
      if(this.rootItem)
        return this.isNCR && this.rootItem.isNCRLocked

      return this.isNCR && this.orderItem.isNCRLocked
    },
    isItemNCRMode() {
      if(this.item.isDeliveryItem || this.item.isMinInvoiceItem || this.item.isCancelFeeItem)
        return false
      if(!this.rootItem)
        return this.isNCR && !this.item.isNCRLocked
      return false
    },
    isItemFlaggedNCR() {
      if(this.item.isDeliveryItem || this.item.isMinInvoiceItem || this.item.isCancelFeeItem)
        return false
      if(this.item.ncrRequestReason == '' || this.item.ncrRequestReason == null)
        return false
      return true
    },
    ncrIconClass() {
      if(this.item.orderApproval && this.item.orderApproval.approvalStatus == ApprovalTypes.approved) {
        return 'order-item-icon-ncr status-approved'
      }
      if(this.item.orderApproval && this.item.orderApproval.approvalStatus == ApprovalTypes.denied) {
        if(this.item.orderApproval.rejectedQuantity == this.item.quantity)
          return 'order-item-icon-ncr status-denied'
        return 'order-item-icon-ncr status-mixed'
      }
      if(this.item.orderApproval && this.item.orderApproval.approvalStatus == ApprovalTypes.pending) {
        return 'order-item-icon-ncr status-pending'
      }
      return 'order-item-icon-ncr'
    },
    isItemCancelled() {
      let rootParentItem = this.findRootParent(this.item)
      if(rootParentItem && rootParentItem.isCancelled)
        return true
      return this.item.isCancelled
    },
    isEditorGeneratedItem() {
      let rootParentItem = this.findRootParent(this.item)
      if(rootParentItem && rootParentItem.isEditorGeneratedItem)
        return true
      return this.item.isEditorGeneratedItem
    },
    isCustomAddedItemForEditorParent() {
      let rootParentItem = this.findRootParent(this.item)
      if(this.item.isDeliveryItem || this.item.isMinPriceItem || this.item.isMinPriceSingleItem || this.item.isMinInvoiceItem || this.item.isCancelFeeItem)
        return false
      if(rootParentItem && rootParentItem.isEditorGeneratedItem && !this.item.isEditorGeneratedItem)
        return true
      return false
    },
    isPriceHidden() {
      // JB 20/01/21: Was hidden for NCR, but removed as causing issues
      return false
    },
    isEditingMode() {
      if(!this.editing)
        return false
      if(this.isItemInNCRLockMode)
        return false
      if(this.item.isCancelled || this.item.isInvoiced)
        return false
      if(this.hasDespatchedQuantity || this.hasInvoicedQuantity)
        return false

      // Check root for details
      let rootParentItem = this.findRootParent(this.item)
      if(rootParentItem) {
        if(rootParentItem.isCancelled || rootParentItem.isInvoiced)
          return false
        if(rootParentItem.despatchedQuantity > 0)
          return false
      }

      return true
    },
    isChildItem() {
      return !this.item.isParentItem
    },
    hasChildren() {
      return this.itemList.filter(childItem => childItem.parentOrderItemId == this.item.id).length > 0
    },
    itemStockLookup() {
      if(this.item.isDeliveryItem)
        return this.stockLookupList.filter(stockLookup => stockLookup.type == this.item.stockType)

      // Services: Filter by the price group - service ranges
      // If the stock has no active price for the price group / service range, then remove it from the stock list
      if(this.item.stockType == InventoryStockTypes.glassService) {
        if(this.parentItem) {        
          let stockPriceGroupId = this.parentItem.stockPriceGroupId
          let servicePriceRangeId = this.item.servicePriceRangeId || this.parentItem.servicePriceRangeId

          return this.stockLookupList.filter(stockLookup => {
            if(stockLookup.type != this.item.stockType)
              return false

            // If no prices data at all, just allow the stock. 
            if(!stockLookup.stockSalesPrices)
              return true

            let activePrices = stockLookup.stockSalesPrices.filter(price => price.isActive)

            let price = activePrices.find(salesPrice => {
              return salesPrice.stockPriceGroupId == stockPriceGroupId && salesPrice.servicePriceRangeId == servicePriceRangeId
            })
            return price != null
          })
        }
      }

      return this.stockLookupList.filter(stockLookup => stockLookup.type == this.item.stockType)
    },
    isRootItem() {
      return this.parentItem == null
    },
    hasGlassParent() {
      if(this.parentItem) {              
        return this.parentItem.stockType == InventoryStockTypes.glass
      }
      return false
    },
    hasTemplateParent() {
      if(this.parentItem) {
        return this.parentItem.stockType == InventoryStockTypes.template
      }
      return false
    },
    hasAssemblyParent() {
      if(this.parentItem) {              
        return this.parentItem.stockType == InventoryStockTypes.assembly
      }
      return false
    },
    isGlassItem() {
      return this.item.stockType == InventoryStockTypes.glass
    },
    isGlassServiceItem() {
      return this.item.stockType == InventoryStockTypes.glassService
    },
    isGeneralItem() {
      return this.item.stockType == InventoryStockTypes.general
    },
    isTemplateItem() {
      return this.item.stockType == InventoryStockTypes.template
    },
    isAssemblyItem() {
      return this.item.stockType == InventoryStockTypes.assembly
    },
    isEdgeworkEditable() {
      if(!this.isEditingMode)
        return false
      if(this.optionSupplierDetailsAvailable)
        return false
      return true
    },
    isWidthHeightAvailable() {
      if(this.isGlassItem || this.isTemplateItem)
        return true

      // If an assembly, only allow if UOM type is square metres
      if(this.item.stockType == InventoryStockTypes.assembly) {
        return this.item.uomType == UOMTypes.metresq
      }
      return false
    },
    isWidthEditable() {
      if(this.item.isGlassSizeLocked)
        return false

      // If glass & a template child, don't be editable unless unlocked
      if(this.item.stockType == InventoryStockTypes.glass && this.hasTemplateParent && this.item.isGlassSizeLocked)
        return false

      // If glass / assembly & a editor item, don't allow size to be editable
      if((this.item.stockType == InventoryStockTypes.glass || this.item.stockType == InventoryStockTypes.assembly) && this.isEditorGeneratedItem)
        return false

      // If an assembly or part of an assembly & assembly rules don't allow size editing, don't allow it
      // Note: Assemblies will copy rules/options to all children
      if(this.item.stockType == InventoryStockTypes.assembly || this.hasAssemblyParent) {
        if(this.item.orderEntryConfigData && this.item.orderEntryConfigData.assemblyOptions) {
          if(!this.item.orderEntryConfigData.assemblyOptions.isSizeEditable)
            return false
        }
      }

      return this.item.stockType == InventoryStockTypes.glass || this.item.stockType == InventoryStockTypes.template || this.item.stockType == InventoryStockTypes.assembly
    },
    isHeightEditable() {
      if(this.item.isGlassSizeLocked)
        return false

      // If glass & a template child, don't be editable unless unlocked
      if(this.item.stockType == InventoryStockTypes.glass && this.hasTemplateParent && this.item.isGlassSizeLocked)
        return false

      // If glass / assembly & a editor item, don't allow size to be editable
      if((this.item.stockType == InventoryStockTypes.glass || this.item.stockType == InventoryStockTypes.assembly) && this.isEditorGeneratedItem)
        return false

      // If an assembly or part of an assembly & assembly rules don't allow size editing, don't allow it
      // Note: Assemblies will copy rules/options to all children
      if(this.item.stockType == InventoryStockTypes.assembly || this.hasAssemblyParent) {
        if(this.item.orderEntryConfigData && this.item.orderEntryConfigData.assemblyOptions) {
          if(!this.item.orderEntryConfigData.assemblyOptions.isSizeEditable)
            return false
        }
      }

      return this.item.stockType == InventoryStockTypes.glass || this.item.stockType == InventoryStockTypes.template || this.item.stockType == InventoryStockTypes.assembly
    },
    isItemEdgeworkService() {
      if(this.item.stockType != InventoryStockTypes.glassService)
        return false
      // Only items with lineal-metres UoM can have edgework
      return this.item.uomType == UOMTypes.length
    },
    isItemPercentageUOMType() {
      return (this.item.uomType == UOMTypes.percentOfLineItem || this.item.uomType == UOMTypes.percentOfOrder)
    },
    isItemUnitsEditable() {
      if(this.isItemPercentageUOMType || this.item.isUnitsLocked)
        return false

      // Cannot edit units for order editor generated items
      if(this.isEditorGeneratedItem)
        return false

      return this.item.stockType == InventoryStockTypes.glassService || this.item.stockType == InventoryStockTypes.general
    },
    isItemUnitPriceEditable() {
      if(!this.isEditingMode || this.item.priceLoading)
        return false
      if(!this.item.unitPriceOverridden || this.isPriceHidden)
        return false
      return true
    },
    isItemStockUnlockable() {
      if(!this.isEditingMode || this.item.isDeliveryItem || this.item.isMinInvoiceItem || !this.item.isStockLocked || this.item.isCancelFeeItem)
        return false
      return true
    },
    isItemUnitsUnlockable() {
      if(!this.isEditingMode || this.isItemPercentageUOMType || this.item.isDeliveryItem || this.item.isMinPriceItem || this.item.isMinPriceSingleItem || this.item.isMinInvoiceItem || this.item.isCancelFeeItem)
        return false
        
      // Cannot edit units for order editor generated items
      if(this.isEditorGeneratedItem)
        return false

      return this.item.stockType == InventoryStockTypes.glassService || this.item.stockType == InventoryStockTypes.general
    },
    isItemUnitPriceUnlockable() {
      if(!this.isEditingMode || this.isTemplateItem || this.item.isMinPriceItem || this.item.isMinPriceSingleItem || this.item.isMinInvoiceItem || this.item.isCancelFeeItem)
        return false
      return true
    },
    isItemGlassSizeUnlockable() {
      if(!this.isEditingMode || !this.isGlassItem || !this.item.isGlassSizeLocked)
        return false

      // Cannot edit size for order editor generated items
      if(this.isEditorGeneratedItem)
        return false

      // If an assembly or part of an assembly & assembly rules don't allow size editing, don't allow it
      // Note: Assemblies will copy rules/options to all children
      if(this.hasAssemblyParent) {
        if(this.item.orderEntryConfigData && this.item.orderEntryConfigData.assemblyOptions) {
          if(!this.item.orderEntryConfigData.assemblyOptions.isSizeEditable)
            return false
        }
      }

      return true
    },
    isItemPriceGroupUnlockable() {
      if(!this.isEditingMode || !this.isGlassItem || !this.item.isPriceGroupLocked)
        return false
      return true
    },
    isItemQuantityUnlockable() {
      if(!this.isQuantityEditable(this.item) || !this.item.isQuantityLocked || this.item.isFixed)
        return false

      // Cannot adjust quantities directly for NCRs
      // They must go through the NCR dialog
      if(this.isNCR)
        return false
      return true
    },
    isItemRemovable() {
      if(this.item.isFixed)
        return false
      if(this.$props.optionBlockRemovingExternal && this.item.isExternal && !this.item.isNewItem)
        return false
      if(this.$props.optionSupplierDetailsAvailable && !this.item.isExternal)
        return false
      if(this.item.isDeliveryItem || this.item.isMinPriceItem || this.item.isMinPriceSingleItem || this.item.isMinInvoiceItem || this.item.isCancelFeeItem)
        return false
      if(this.hasDespatchedQuantity)
        return false
      if(this.hasInvoicedQuantity)
        return false

      // Check assembly rules on the item
      if(this.orderItem.orderEntryConfigData && this.orderItem.orderEntryConfigData.assemblyItemData) {
        if(this.orderItem.orderEntryConfigData.assemblyItemData.isRemoveable === false)
          return false
      }

      return true
    },
    isItemCancellable() {
      if(this.isEditingMode || this.item.isDeliveryItem || this.item.isMinInvoiceItem || this.isChildItem || !this.optionCancelItemAvailable || this.item.isCancelled || this.item.isInvoiced || this.item.isCancelFeeItem)
        return false
      return true
    },
    hasDespatchedQuantity() {
      return this.item.despatchedQuantity > 0
    },
    hasInvoicedQuantity() {
      return this.item.invoicedQuantity > 0
    },
    isItemPreviewAvailable() {
      if(this.isEditingMode || (!this.isGlassItem && !this.isAssemblyItem) || !this.accountHasOrderEditor || !this.item.svgDisplayData)
        return false
      if(!this.optionPreviewItemAvailable)
        return false
      return true
    },
    isItemExternalAvailable() {
      if(this.item.isDeliveryItem || this.item.isMinPriceItem || this.item.isMinPriceSingleItem || this.item.isMinInvoiceItem || this.item.isCancelFeeItem)
        return false

      return true
    },
    isPriceGroupsAvailable() {
      return this.showPriceGroups
    },
    isMarkAsAvailable() {
      return !this.$props.optionSupplierDetailsAvailable && this.showMarkAs
    },
    isMarkAsEditable() {
      if(this.isGlassItem || this.isTemplateItem || this.isGeneralItem || this.isAssemblyItem)
        return true
      return false
    },
    isUnitsAvailable() {
      return this.showUnits
    },
    isTotalUnitsAvailable() {
      return this.showUnits
    },
    isSupplierDetailsAvailable() {
      return this.$props.optionSupplierDetailsAvailable
    },
    stockPriceGroupLookup() {
      return this.$props.stockPriceGroupList
        .filter(priceGroup => priceGroup.isActive)
        .map(priceGroup => {
          return {
            value: priceGroup.id,
            name: priceGroup.name
          }
        })
    },
    itemPriceDescription() {
      var priceDescription = ''

      // If this is a child item, fill in the service details from the parent
      if(this.item.stockType == InventoryStockTypes.glassService) {
        if(this.parentItem) {              
          var serviceRange = ''

          if(this.parentItem.stockPriceGroup && this.parentItem.stockPriceGroup.name) {
            priceDescription = this.parentItem.stockPriceGroup.name
          }
          if(this.parentItem.servicePriceRange && this.parentItem.servicePriceRange.name) {
            serviceRange = this.parentItem.servicePriceRange.name
          }
          if(this.item.servicePriceRange && this.item.servicePriceRange.name) {
            serviceRange = this.item.servicePriceRange.name
          }

          priceDescription += ` | ${serviceRange}`
        }

        return priceDescription
      }

      // Display delivery fee info for delivery item / min price item / min invoice item / cancel fee item
      if(this.item.isDeliveryItem || this.item.isMinPriceItem || this.item.isMinPriceSingleItem || this.item.isMinInvoiceItem || this.item.isCancelFeeItem) {
        return this.item.pricingDescription
      }

      if(this.item.stockPriceGroup && this.item.stockPriceGroup.name)
        return this.item.stockPriceGroup.name
      return '-'
    },
    isStockLinkedPriceGroup() {
      if(!this.item.stockPriceGroup || !this.item.stockPriceGroupId)
        return false
      return this.item.stockPriceGroup.isStockLinked
    },
    isGlassLevelSelected() {
      return this.item.glassLevelId != null
    },
    isGlassSizeSelected() {
      return this.item.glassSizeId != null
    },
    indentDisplay() {
      return '&nbsp;'.repeat(this.item.level * 2)
    },
    isItemDiscounted() {
      return this.item.discountRate > 0
    },
    itemDiscountTooltip() {
      var tooltip = `${this.$options.filters.formatCurrency(this.item.discountAmount)} Discount`

      if(this.item.units * this.item.quantity > 0) {
        var perUnit = Utils.roundCurrency(this.item.discountAmount / (this.item.units * this.item.quantity))
        tooltip += ` (${this.$options.filters.formatCurrency(perUnit)} / unit)`
      }

      return tooltip
    },
    isUnitPriceAvailable() {
      return this.showPricing
    },
    isLinePriceAvailable() {
      return this.showPricing
    },
    itemTotalUnits() {
      if(this.item.isBlock) {
        return Utils.roundQuantityValue(this.item.units * (this.item.blockSize * this.item.quantity))
      }
      return Utils.roundQuantityValue(this.item.units * this.item.quantity)
    },
    itemSupplierDescription() {
      if(!this.item.supplierContactId)
        return ''
      return Utils.getLookupName(this.supplierLookupList, this.item.supplierContactId)
    },
    isCustomerPricing() {
      return this.priceType == PriceTypes.customer
    },
    isSupplierPricing() {
      return this.priceType == PriceTypes.supplier
    },
    accountHasOrderEditor() {
      return this.accountHasProductFeature(this.AccountProductFeatureTypes.orderEditor)
    },
    isUnitPriceAboveOriginalPrice() {
      if(!this.item.unitPriceOverridden)
        return false
      if(isNaN(this.item.unitPrice) || isNaN(this.item.originalUnitPrice))
        return false
      return this.item.unitPrice > this.item.originalUnitPrice
    },
    itemTotalWeight() {
      if(!this.item.stock)
        return 0
      return Utils.roundNumber(Utils.calculateGlassWeightForArea(this.itemTotalUnits, this.item.stock.thickness))
    },
    hasCustomFields() {
      return _.isObject(this.item.customFieldData) && !_.isEmpty(this.item.customFieldData)
    },
    customFieldDisplayData() {
      if(_.isArray(this.item.customFieldData)) {
        return 'Fields: ' + this.item.customFieldData.map(field => {
          return `${field.key}: ${field.value}`
        }).join(', ')
      }
      return ''
    },
    customFieldEditList() {
      if(_.isArray(this.item.customFieldData))
        return this.item.customFieldData
      return []
    },
    customFieldLookup() {
      if(_.isArray(this.customFieldList)) {
        return this.customFieldList.map(field => {
          return {
            value: field,
            name: field
          }
        })
      }
      return []
    },
    isCustomFieldLookupEmpty() {
      return this.customFieldLookup.length == 0
    },
    isAddServiceChildItemAvailable() {
      if(this.isGlassItem && !this.item.isFixed && this.optionAddServiceAvailable) {
        // Check assembly rules if part of an assembly
        if(_.isObject(this.item.orderEntryConfigData) && _.isObject(this.item.orderEntryConfigData.assemblyOptions)) {
          if(!this.item.orderEntryConfigData.assemblyOptions.isAdditionalItemsEnabled)
            return false
        }

        return true
      }
      if(this.isGlassServiceItem && !this.item.isFixed && this.optionAddServiceAvailable) {
        return true
      }
      return false
    },
    isAddGeneralChildItemAvailable() {
      if(this.isGlassItem && !this.isStockLinkedPriceGroup && !this.item.isFixed && this.optionAddGeneralAvailable) {
        // Check assembly rules if part of an assembly
        if(_.isObject(this.item.orderEntryConfigData) && _.isObject(this.item.orderEntryConfigData.assemblyOptions)) {
          if(!this.item.orderEntryConfigData.assemblyOptions.isAdditionalItemsEnabled)
            return false
        }

        return true
      }
      return false
    },
    isCopyGroupAvailable() {
      if((this.isGlassItem || this.isAssemblyItem || this.isTemplateItem) && this.isRootItem && !this.isEditorGeneratedItem && this.optionNewItemGlassAvailable && !this.isNCR) {
        return true 
      }

      return false
    },
  },
  methods: {
    itemListUpdated() {
      this.$emit('update', this.$data.item)
    },
    itemPricingConfigUpdated() {
      this.$emit('pricingConfigUpdate', this.$data.item)
    },
    // Precalculate the filtered stock lookup list
    // Precalculated for performance reasons
    calculateStockLookupList() {
      if(this.item.isDeliveryItem) {
        this.filteredStockLookupList = this.stockLookupList.filter(stockLookup => stockLookup.type == this.item.stockType)
        return
      }

      var hasStockFilterMap = false
      var stockIdFilterMap = {}

      // If part of an assembly, check if there are limits to what stock items are available
      if(this.orderItem.orderEntryConfigData && this.orderItem.orderEntryConfigData.assemblyItemData) {
        if(_.isArray(this.orderItem.orderEntryConfigData.assemblyItemData.stockList) && this.orderItem.orderEntryConfigData.assemblyItemData.stockList.length > 0) {

          hasStockFilterMap = true
          this.orderItem.orderEntryConfigData.assemblyItemData.stockList.forEach(stockId => {
            stockIdFilterMap[stockId] = true
          })
        }
      }

      // Services: Filter by the price group - service ranges
      // If the stock has no active price for the price group / service range, then remove it from the stock list
      if(this.item.stockType == InventoryStockTypes.glassService) {
        if(this.parentItem) {        
          let stockPriceGroupId = this.parentItem.stockPriceGroupId
          let servicePriceRangeId = this.item.servicePriceRangeId || this.parentItem.servicePriceRangeId

          this.filteredStockLookupList = this.stockLookupList.filter(stockLookup => {
            if(stockLookup.type != this.item.stockType)
              return false

            // Check if we're limited to certain stocks
            // Eg, assembly rules
            if(hasStockFilterMap && !stockIdFilterMap[stockLookup.value]) {
              return false
            }

            // If no prices data at all, just allow the stock. 
            if(!stockLookup.stockSalesPrices)
              return true

            let activePrices = stockLookup.stockSalesPrices.filter(price => price.isActive)

            let price = activePrices.find(salesPrice => {
              return salesPrice.stockPriceGroupId == stockPriceGroupId && salesPrice.servicePriceRangeId == servicePriceRangeId
            })
            return price != null
          })
          return
        }
      }

      this.filteredStockLookupList = this.stockLookupList.filter(stockLookup => {
        if(stockLookup.type != this.item.stockType)
          return false

        // Check if we're limited to certain stocks
        // Eg, assembly rules
        if(hasStockFilterMap && !stockIdFilterMap[stockLookup.value])
          return false
        
        return true
      })
    },
    // Precalculate the filtered price group lookup list
    // Precalculated for performance reasons
    calculateStockPriceGroupLookupList() {
      let stock = this.item.stock

      var hasPriceGroupFilterMap = false
      var stockPriceGroupIdFilterMap = {}

      // If part of an assembly, check if there are limits to what price groups are available
      if(this.orderItem.orderEntryConfigData && this.orderItem.orderEntryConfigData.assemblyItemData) {
        if(_.isArray(this.orderItem.orderEntryConfigData.assemblyItemData.pricingGroupList) && this.orderItem.orderEntryConfigData.assemblyItemData.pricingGroupList.length > 0) {

          hasPriceGroupFilterMap = true
          this.orderItem.orderEntryConfigData.assemblyItemData.pricingGroupList.forEach(stockPriceGroupId => {
            stockPriceGroupIdFilterMap[stockPriceGroupId] = true
          })
        }
      }

      this.filteredStockPriceGroupLookupList = this.$props.stockPriceGroupList
        .filter(priceGroup => {
          if(!priceGroup.isActive)
            return false

          // Allow all for supplier pricing types
          // JB: 16/12/2020 - Added in during PO creations when going live
          if(this.isSupplierPricing)
            return true

          // Check filter map (assemblies)
          if(hasPriceGroupFilterMap && !stockPriceGroupIdFilterMap[priceGroup.id])
            return false

          if(!stock.availablePriceGroups || stock.availablePriceGroups.length == 0)
            return false
          return stock.availablePriceGroups.indexOf(priceGroup.id) != -1
        })
        .map(priceGroup => {
          return {
            value: priceGroup.id,
            name: priceGroup.name
          }
        })
    },
    setItemStockId(stockId, options = {}) {
      return new Promise((resolve, reject) => {
        _.defaults(options, {
          updateList: true,
          selectInputs: true,
          reset: true,
          ignoreTemplateUpdate: false,
          ignoreAssemblyUpdate: false,
          batchRequest: false
        })

        // If the stock item changed, lock fields again
        // Only if reset is set
        if(stockId != this.item.stockId && options.reset) {
          this.item.unitPriceOverridden = false
          this.item.isUnitsLocked = true
        }

        this.item.stockId = stockId

        if(options.reset) {
          // Reset glass level selection
          this.item.glassLevelId = null
          this.item.glassLevel = {}
          this.item.glassSizeId = null
          this.item.glassSize = {}

          // Reset pricing details on stock change
          this.item.stockPriceGroup = {}
          this.item.stockPriceGroupId = null
          this.item.servicePriceRange = {}
          this.item.servicePriceRangeId = null

          this.item.defaultEdgeworkConfig = null
          if(!this.item.orderEntryConfigData)
            this.item.orderEntryConfigData = {}
          this.item.orderEntryConfigData.defaultEdgeworkProcessed = false
        }

        this.item.templateList = []
        this.item.assemblyData = {}

        if(this.item.stockId == null) {
          // If a template / assembly, notify list
          if(this.item.stockType == InventoryStockTypes.template && !options.ignoreTemplateUpdate) {
            this.$emit('templateUpdate', this.item)
          }
          if(this.item.stockType == InventoryStockTypes.assembly && !options.ignoreAssemblyUpdate) {
            this.$emit('assemblyUpdate', this.item)
          }

          this.item.loading = false
          if(options.updateList) {
            this.itemListUpdated()
            this.itemPricingConfigUpdated()
          }
          return resolve()
        }

        this.item.loading = true

        this.$apiService.orderItemService.getStock(this.item.stockId, { batchRequest: options.batchRequest })
        .then(response => {
          let stock = response.data
          let oldItemInfo = _.clone(this.item)

          this.item.stock = {
            id: stock.id,
            code: stock.code,
            name: stock.name,
            isTrackingQuantity: stock.isTrackingQuantity,
            isProductionCompletable: stock.isProductionCompletable,
            thickness: stock.thickness || 0,
            quantity: stock.quantity || 0,
            glassType: stock.glassType,
            isStockUsingFaces: stock.isStockUsingFaces,
            isStockUsingGrain: stock.isStockUsingGrain,
            stockMaterialType: stock.stockMaterialType,
            stockColour1: stock.stockColour1,
            stockColour2: stock.stockColour2,
            stockColourOpacity: stock.stockColourOpacity,
            uomType: stock.uomType,
            assemblyData: stock.assemblyData,
            availablePriceGroups: []
          }
          if(options.reset)
            this.item.description = stock.name
          this.item.uomType = stock.uomType
          this.item.uomDisplay = Utils.getLookupName(UOMTypeUnitsLookup, stock.uomType)
          this.item.templateList = stock.templateList || []
          this.item.assemblyData = stock.assemblyData || {}

          // If glass, build a list of sizes / stock levels available
          if(stock.type == InventoryStockTypes.glass) {
            this.item.stock.availableGlass = this.buildAvailableGlassList(stock)
          }

          // If 'Each' unit type, unlock units by default if selectInputs (ie, manual entry) if set
          if(this.item.uomType == UOMTypes.each && options.selectInputs) {
            if(this.item.units == 0)
              this.item.units = 1
            // If the UOM type changed, reset the units
            if(oldItemInfo.uomType && oldItemInfo.uomType != this.item.uomType)
              this.item.units = 1

            this.item.isUnitsLocked = false
            this.item.edgeworkFlagList = []   // Reset edgework flags as they are no longer used
          }

          // Build list of price groups that are active for the stock
          if(stock.glassSalesPriceList) {
            this.item.stock.availablePriceGroups = stock.glassSalesPriceList
            .filter(salesPrice => salesPrice.isActive)
            .map(salesPrice => salesPrice.stockPriceGroupId)

            // Recalculate the available price group list
            this.calculateStockPriceGroupLookupList()
          }

          // Attempt to download pricing data straight away
          this.priceGroupUpdated(this.item.stockPriceGroupId, true, { batchRequest: options.batchRequest })
          .finally(() => {
            // Move selection across after price details loaded
            if(options.selectInputs) {
              if(this.item.stockType == InventoryStockTypes.general || this.item.stockType == InventoryStockTypes.glassService || this.item.stockType == InventoryStockTypes.assembly) {
                if(this.$refs.stockDescription)
                  this.$refs.stockDescription.focus()
              }
            }
          })
        })
        .finally(() => {
          this.item.loading = false
          if(options.updateList) {
            this.itemListUpdated()
            this.itemPricingConfigUpdated()

            // If a template / assembly, setup the child items
            if(this.item.stockType == InventoryStockTypes.template && !options.ignoreTemplateUpdate) {
              this.$emit('templateUpdate', this.item)
            }
            if(this.item.stockType == InventoryStockTypes.assembly && !options.ignoreAssemblyUpdate) {
              this.$emit('assemblyUpdate', this.item)
            }
          }

          this.$nextTick(() => {
            if(options.selectInputs) {
              if(this.$refs.stockDescription)
                this.$refs.stockDescription.focus()
            }
          })

          return resolve()
        })
      })
    },

    buildAvailableGlassList(stock) {
      if(!stock.glassSizeList)
        return []

      var levelList = []
      stock.glassSizeList.forEach(glassSize => {
        if(glassSize.glassLevels) {
          glassSize.glassLevels.forEach(glassLevel => {
            levelList.push({
              id: glassLevel.id,
              width: glassLevel.width,
              height: glassLevel.height,
              available: glassLevel.summary.totalAvailable
            })
          })
        }
      })
      return levelList
    },

    selectStockEntry() {
      this.$refs.stockEntry.select()
      this.$refs.stockEntry.focus()
    },
    selectQuantityEntry() {
      this.$refs.quantity.select()
      this.$refs.quantity.focus()
    },
    priceGroupUpdated(stockPriceGroupId, selectInputs = true, options = {}) {
      return new Promise((resolve, reject) => {
        if(this.item.stockId == null)
          return resolve()
        if(this.item.stockType == InventoryStockTypes.template)
          return resolve()

        // If the price group changed, lock fields again
        if(stockPriceGroupId != this.item.stockPriceGroupId) {
          this.item.unitPriceOverridden = false
          this.item.isUnitsLocked = true

          // Reset some info before loading new data
          this.item.defaultEdgeworkConfig = null
          if(!this.item.orderEntryConfigData)
            this.item.orderEntryConfigData = {}
          this.item.orderEntryConfigData.defaultEdgeworkProcessed = false
        }

        var showPriceErrors = true
        var thickness = 0
        this.item.stockPriceGroupId = stockPriceGroupId
        this.item.stockPriceGroup = {}
        let requiresPriceGroup = (this.item.stockType != InventoryStockTypes.general && this.item.stockType != InventoryStockTypes.template && this.item.stockType != InventoryStockTypes.assembly)

        if(this.item.stockType == InventoryStockTypes.glass && this.item.stock)
          thickness = this.item.stock.thickness
        
        // If this is a glassService, pull in the details from the parent glass item
        if(this.item.stockType == InventoryStockTypes.glassService) {
          if(this.parentItem) {
            this.item.stockPriceGroupId = this.parentItem.stockPriceGroupId
            this.item.servicePriceRangeId = this.parentItem.servicePriceRangeId
            thickness = this.parentItem.stock.thickness
          }
        }

        // If in supplier mode & not external don't display pricing errors
        // Still need to run a query for service range details, etc
        if(this.$props.optionSupplierDetailsAvailable && !this.item.isExternal) {
          showPriceErrors = false
        }

        // Run price lookup
        if(this.item.stockPriceGroupId != null || !requiresPriceGroup) {

          // Lookup the unit price for the stock / price group / customer combination
          this.item.priceLoading = true
          this.item.priceError = ''

          let stockPriceGroup = this.$props.stockPriceGroupList.find(priceGroup => priceGroup.id == this.item.stockPriceGroupId)

          // Set the price group description for glass items
          if(this.item.stockType == InventoryStockTypes.glass) {
            this.item.generatedDescription = stockPriceGroup.stockDescription || ''
          }

          this.item.stockPriceGroup = _.clone(stockPriceGroup || {})

          // If this isn't a stock linked item, clear out the glass level/size
          if(!this.isStockLinkedPriceGroup) {
            this.item.glassLevelId = null
            this.item.glassLevel = {}
            this.item.glassSizeId = null
            this.item.glassSize = {}
            this.item.isBlock = false
          }

          var contactId = this.$props.contactId
          if(this.$props.priceType == PriceTypes.supplier && this.item.supplierContactId)
            contactId = this.item.supplierContactId

          // Pricing lookup
          let stockLookupFilter = {
            priceType: this.$props.priceType,
            contactId: contactId,
            defaultSalesRateId: this.$props.newContactSalesRateId,
            stockId: this.item.stockId,
            stockPriceGroupId: this.item.stockPriceGroupId,
            thickness: thickness
          }

          Promise.all([
            this.$apiService.orderItemService.getContactStockLookup(stockLookupFilter, { batchRequest: options.batchRequest }),
            this.$apiService.orderItemService.getProductionStockLookup(stockLookupFilter, { batchRequest: options.batchRequest })
          ])
          .then(results => {
            let priceData = results[0].data
            let productionData = results[1].data

            if(!this.item.unitPriceOverridden)
              this.item.unitPrice = parseFloat(priceData.price.value)
            this.item.originalUnitPrice = parseFloat(priceData.price.value)
            this.item.minChargePrice = parseFloat(priceData.price.minChargePrice)
            this.item.minChargeItemPrice = parseFloat(priceData.price.minChargeItemPrice)
            this.item.minChargeQuantity = parseFloat(priceData.price.minChargeQuantity)
            this.item.minChargeInvoice = parseFloat(priceData.price.minChargeInvoice)

            if(priceData.price.error && showPriceErrors) {
              // Pricing error
              this.item.priceError = priceData.price.error
            } else {
              this.item.priceError = ''
            }

            if(priceData.servicePriceRange)
              this.item.servicePriceRangeId = priceData.servicePriceRange.id

            this.item.servicePriceRange = priceData.servicePriceRange || {}

            // Go through production details for new items
            // Don't modify existing items
            if(this.item.isNewItem) {
              let productionIsExternal = false
              if(productionData && _.isArray(productionData.productionStages)) {
                productionIsExternal = productionData.productionStages.find(prodStage => prodStage.isExternal) != null
              }
              if(productionIsExternal)
                this.item.isExternal = true
            }

            // Default edgework info
            // Used by OrderItemList for default edgeworks services
            this.item.defaultEdgeworkConfig = productionData.defaultEdgeworkConfig
          })
          .catch(err => {
            console.error(err)
            // TODO: General error about the lookup
            if(showPriceErrors)
              this.item.priceError = _.get(err.response, 'data.error.message') || 'Error retrieving price data'
          })
          .finally(() => {

            this.item.priceLoading = false
            this.itemListUpdated()
            this.itemPricingConfigUpdated()

            return resolve()
          })
        } else {
          this.$nextTick(() => {
            this.itemPricingConfigUpdated()
          })

          return resolve()
        }

        this.$nextTick(() => {
          if(this.item.stockPriceGroupId != null && selectInputs) {
            if(this.$refs.quantity) {
              this.$refs.quantity.select()
              this.$refs.quantity.focus()
            }
          }
          this.itemListUpdated()
        })
      })
    },
    supplierUpdated() {
      this.itemListUpdated()
      
      return this.priceGroupUpdated(this.item.stockPriceGroupId, false)
    },
    priceGroupSelectFilter(option, label, search) {
      return (label || '').toLowerCase().indexOf(search.toLowerCase()) == 0
    },
    stockPriceGroupLookupForItem(item) {
      let stock = item.stock

      // TODO: If inside a template, check what price groups the template has available for this item
      if(this.parentItem && this.parentItem.stockType == InventoryStockTypes.template) {
        // TODO: Find this item index with parent
        // TODO: Get template stock price availability list
        
      }

      return this.$props.stockPriceGroupList
        .filter(priceGroup => {
          if(!priceGroup.isActive)
            return false
          if(!stock.availablePriceGroups || stock.availablePriceGroups.length == 0)
            return false
          return stock.availablePriceGroups.indexOf(priceGroup.id) != -1
        })
        .map(priceGroup => {
          return {
            value: priceGroup.id,
            name: priceGroup.name
          }
        })
    },
    displayEdgeworkPopover(evt) {
      this.$refs.edgeworkPopover.displayFromElement(evt.target)
    },
    displayExternalPopover(evt) {
      this.$refs.externalPopover.displayFromElement(evt.target)
    },
    displayNCRPopover(evt) {
      // If in editing mode, display the dialog
      // If in view mode, show the popover
      if(!this.editing)
        this.$refs.ncrPopover.displayFromElement(evt.target)
      else {
        let originalQuantity = parseInt(this.item.ncrOriginalQuantity)

        this.displayModal(OrderItemNCRDialog, { 
          orderItem: this.item,
          originalQuantity: originalQuantity,
          isNewOrder: this.isNewOrder,
          submitTitle: 'Update',
          submitPromise: (data) => {
            return new Promise((resolve, reject) => {
              this.item.ncrLabelList = data.ncrLabelList
              this.item.ncrRequestReason = data.ncrRequestReason
              this.item.quantity = data.quantity
              this.itemListUpdated()

              return resolve()
            })          
          }
        })
        .then((success) => {
        })
      }
    },
    ncrRepairClicked(evt) {
      this.$emit('toggleNCRIsRepair')
    },
    itemEdgeworkLabel(item) {
      if(!item.edgeworkFlagList)
        return ''
      var description = 'None'
      if(item.edgeworkFlagList.length == 4)
        description = 'All Round'
      else if(item.edgeworkFlagList.length > 0)
        description = item.edgeworkFlagList.map(flag => Utils.getLookupName(OrderItemEdgeworkTypeLookup, flag)).join(', ')

      return `Edgework: ${description}`
    },
    displayNotesPopover(evt) {
      this.$refs.notesPopover.displayFromElement(evt.target)
      this.$nextTick(() => {
        this.$refs.notesEntry.focus()
      })
    },
    displayCustomFieldsPopover(evt) {
      this.$refs.customFieldsPopover.displayFromElement(evt.target)
    },
    displayDiscountPopover(evt) {
      this.$refs.discountPopover.displayFromElement(evt.target)
      this.$nextTick(() => {
        this.$refs.discountRateEntry.select()
        this.$refs.discountRateEntry.focus()
      })
    },
    displayPriceErrorPopover(evt) {
      this.$refs.priceErrorPopover.displayFromElement(evt.target)
    },
    displayProductionErrorPopover(evt) {
      this.$refs.productionErrorPopover.displayFromElement(evt.target)
    },
    displayItemCancelledPopover(evt) {
      this.$refs.cancelledItemPopover.displayFromElement(evt.target)
    },
    findRootParent(item) {
      var rootItem = null

      if(item.parentOrderItemId == null)
        return item

      this.itemList.forEach(parentItem => {
        if(parentItem.id == item.parentOrderItemId) {
          let root = this.findRootParent(parentItem)
          if(root)
            rootItem = root
        }
      })
      return rootItem
    },
    findFullChildItemList(item, outList = []) {
      this.itemList.forEach(childItem => {
        if(childItem.parentOrderItemId == item.id) {
          outList.push(childItem)
          this.findFullChildItemList(childItem, outList)
        }
      })
      return outList
    },
    isEntireRootItemRequireExternal() {
      if(!this.item.isExternal)
        return false

      // Check all parent/children for requiring external. If everything does, this entire root item is externally sourced
      // Only handles items that are valid for external
      let rootParentItem = this.findRootParent(this.item)
      let childItemList = this.findFullChildItemList(rootParentItem).filter(childItem => {
        if(childItem.isDeliveryItem || childItem.isMinPriceItem || childItem.isMinPriceSingleItem || childItem.isMinInvoiceItem || childItem.isCancelFeeItem)
          return false
        return true
      })

      if(rootParentItem.isExternal && childItemList.filter(item => item.isExternal).length == childItemList.length)
        return true

      return false
    },
    allChildrenRequireExternal(item) {
      if(!item.isExternal)
        return false

      let childItemList = this.itemList.filter(childItem => childItem.parentOrderItemId == item.id)
      
      return childItemList.filter(childItem => this.allChildrenRequireExternal(childItem)).length == childItemList.length
    },
    externalClicked(evt) {
      if(this.isEditingMode) {
        this.$emit('toggleExternal')
      } else {
        this.$refs.externalPopover.displayFromElement(evt.target)
      }
    },
    isQuantityEditable(item) {
      // Cannot edit quantity for template children. Template controls all the values
      let rootParent = this.findRootParent(item)
      if(item.stockType != InventoryStockTypes.template && rootParent && rootParent.stockType == InventoryStockTypes.template)
        return false

      // Cannot edit quantity for assembly children. Units can be modified as required
      if(item.stockType != InventoryStockTypes.assembly && rootParent && rootParent.stockType == InventoryStockTypes.assembly)
        return false

      return this.isEditingMode && !this.hasGlassParent && !this.item.isFixed && !this.item.isDeliveryItem && !this.item.isMinPriceItem && !this.item.isMinPriceSingleItem && !this.item.isMinInvoiceItem && !this.item.isCancelFeeItem
    },
    unlockItemStock() {
      this.item.isStockLocked = false
    },
    unlockItemUnits() {
      this.item.isUnitsLocked = false
    },
    unlockItemUnitPrice() {
      this.item.unitPriceOverridden = true
    },
    unlockItemGlassSize() {
      this.item.isGlassSizeLocked = false
    },
    unlockItemPriceGroup() {
      this.item.isPriceGroupLocked = false
    },
    unlockItemQuantity() {
      this.item.isQuantityLocked = false
    },
    itemResetUnitPrice() {
      this.item.unitPriceOverridden = false
      this.item.unitPrice = this.item.originalUnitPrice
      this.itemListUpdated()
    },
    itemResetUnits() {
      this.item.isUnitsLocked = true
      this.itemListUpdated()
    },
    itemResetGlassSize() {
      this.item.isGlassSizeLocked = true
      this.itemListUpdated()
    },

    updateItemLoadingStatus() {
      this.$emit('itemLoadingUpdate', this.item.loading || this.item.priceLoading)
    },
    updateCustomFieldKey(index, value) {
      this.item.customFieldData[index].key = value

      this.itemListUpdated()
    },
    updateCustomFieldValue(index, value) {
      this.item.customFieldData[index].value = value

      this.itemListUpdated()
    },
    removeCustomFieldRow(index) {
      this.item.customFieldData.splice(index, 1)

      this.itemListUpdated()
    },
    addCustomFieldRow() {
      if(!_.isArray(this.item.customFieldData))
        this.item.customFieldData = []

      this.item.customFieldData.push({
        key: '',
        value: ''
      })

      this.itemListUpdated()
    }
  }
}
</script>